import { Calendar, getCalendarEntry, getDefaultCalendar } from 'owa-calendar-cache';
import { getCalendars, getSelectedCalendars } from 'owa-calendar-surface-calendars-store';

import type { CalendarGroup } from 'owa-graph-schema';
import { MAX_CALENDARS_FOR_TOP_CALENDARS_LIST } from '../utils/constants';
import { assertNever } from 'owa-assert';
import { isFeatureEnabled } from 'owa-feature-flags';

export enum GetCalendarsListType {
    All,
    TopAndSelected,
    SortedAll,
}

export default function getCalendarsForCalendarGroup(
    userIdentity: string,
    calendarGroup: CalendarGroup,
    listType: GetCalendarsListType = GetCalendarsListType.All
): Calendar[] {
    const defaultCalendar = getDefaultCalendar(userIdentity);
    const defaultCalendarEntry = defaultCalendar && getCalendarEntry(defaultCalendar);
    const groupHasDefaultCalendar =
        !!defaultCalendarEntry &&
        calendarGroup.serverGroupId === defaultCalendarEntry.ParentGroupId;

    const calendars = getCalendars().filter(item => {
        const calendarEntry = getCalendarEntry(item);

        if (
            defaultCalendarEntry &&
            calendarEntry &&
            defaultCalendarEntry.calendarId.id === calendarEntry.calendarId.id &&
            !isFeatureEnabled('cal-calendarPickerReorder')
        ) {
            // Filter out the default calendar to assure it appears on top.
            return false;
        } else if (
            calendarGroup.calendarGroupId.mailboxInfo.type === 'GroupMailbox' ||
            calendarGroup.calendarGroupId.mailboxInfo.type === 'TeamsMailbox'
        ) {
            // because we assigned a unique parentGroupId per account when we initialized the groupCalendars, we can just check for it
            return calendarEntry && calendarEntry.ParentGroupId === calendarGroup.serverGroupId;
        } else {
            return (
                calendarEntry &&
                calendarGroup.serverGroupId === calendarEntry.ParentGroupId &&
                (!userIdentity || item.id.mailboxInfo.userIdentity === userIdentity)
            );
        }
    });

    switch (listType) {
        case GetCalendarsListType.All:
            if (groupHasDefaultCalendar && !isFeatureEnabled('cal-calendarPickerReorder')) {
                calendars.unshift(defaultCalendar);
            }
            return calendars;
        case GetCalendarsListType.TopAndSelected:
            return groupHasDefaultCalendar
                ? getTopAndSelectedCalendars(calendars, defaultCalendar)
                : getTopAndSelectedCalendars(calendars);
        case GetCalendarsListType.SortedAll:
            if (groupHasDefaultCalendar) {
                calendars.push(defaultCalendar);
            }
            return calendars.sort((cal1, cal2) => cal1.displayName.localeCompare(cal2.displayName));
        default:
            return assertNever(listType);
    }
}

export function getTopAndSelectedCalendars(
    calendars: Calendar[],
    defaultCalendar: Calendar | undefined = undefined
) {
    const topCalendars = defaultCalendar
        ? calendars.slice(0, MAX_CALENDARS_FOR_TOP_CALENDARS_LIST - 1)
        : calendars.slice(0, MAX_CALENDARS_FOR_TOP_CALENDARS_LIST);

    const nonOverlappingSelectedCalendars = getSelectedCalendars()
        .filter(selectedCalendar => {
            // This filters the selected calendars list to return only those selected calendar
            // that overlap with the passed in `calendars`
            const [found] = calendars.filter(cal => cal.id.id === selectedCalendar.id.id);

            return !!found;
        })
        .filter(selectedCalendar => {
            // This filters the list to inlcude all of those selected calendars that
            // are not in the `topCalendars` as we don't want to have duplicate calendars
            // in merged list
            const [found] = topCalendars.filter(cal => cal.id.id === selectedCalendar.id.id);

            return !found;
        });

    // Merge the list and return them with the default calendar first, if applicable
    return defaultCalendar
        ? [defaultCalendar, ...topCalendars, ...nonOverlappingSelectedCalendars]
        : [...topCalendars, ...nonOverlappingSelectedCalendars];
}
